import { useEffect, useMemo, useState } from 'react';
import CustomList from '../../../../core/components/core/Util/List/CustomList/CustomList';
import CustomListContainer from '../../../../core/components/core/Util/List/CustomList/CustomListContainer';
import { loadData } from '../../../../core/components/core/Util/List/CustomList/loadData';
import { getUserSession } from '../../../../core/util/core/Session';
import config from '../../../config/config';
import { chooseProfileFilter } from '../../../helpers/chooseProfileFilter';
import { useDispatch } from 'react-redux';
import sleep from 'sleep-promise';
import moment from 'moment';

function List() {
    const session = getUserSession();
    const now = moment().format('YYYY-MM-DD');
    const profileFilter = chooseProfileFilter(); 
    const pathFrontEnd = '/pcontasaccountsrevenues/';
    const [filterValues, setFilterValues] = useState({});
    const dispatch = useDispatch();
    const paths = useMemo(() => ({
        pathAdd: pathFrontEnd + 'add',
        pathEdit: pathFrontEnd + 'edit',
        pathView: pathFrontEnd + 'view',
        endpointDelete: `${config.endpoints.backend}pcontasaccountsrevenues/delete`,
        endpoint: config.endpoints.backend + 'pcontasaccountsrevenues/getall',
        endpointGetAllEntities: config.endpoints.backend + 'entities/getall',
        endpointGetAllPdcProjects: config.endpoints.backend + 'projects/getall',
        endpointGetAllPcontasCategories: config.endpoints.backend + 'pcontascategories/getall',
        endpointGetAllPcontasSubCategories: config.endpoints.backend + 'pcontassubcategories/getall',
        endpointGetAllOriginResources: config.endpoints.backend + 'pcontasoriginresources/getall'
    }), []);

    async function updateSession(){
        await sleep(10);
        dispatch({ type: 'UPDATE_SESSION', key: 'custom_list_reload', value: new Date() });
    }

    useEffect(() => {
        async function update(){
            await updateSession();
        }    

        update();
    }, [filterValues])

    const clearFilters = (target, filterObject) => {
        const filterName = target['name'];
        if(filterName === 'pdc_entity_id'){
            filterObject = {pdc_entity_id: target['value']};
            if(filterValues.value_start && filterValues.value_end){
                filterObject = {...filterObject, value_start: filterValues.value_start, value_end: filterValues.value_end};
            }
        }else if(filterName === 'pdc_project_id'){
            delete filterObject.pdc_pconta_origin_resource_id;
            delete filterObject.pdc_pconta_category_id;
            delete filterObject.pdc_pconta_sub_category_id;
        }else if(filterName === 'pdc_pconta_category_id'){
            delete filterObject.pdc_pconta_sub_category_id;
        }
        return filterObject;
    }

    const handleChange = async (element) => {
        const target = element['target'];
        let filters = {...filterValues, [target['name']]: target['value']};
        if(target.name === 'period'){
            delete filters.period;
            let firstMonthDate = moment().startOf('month').format('YYYY-MM-DD');
            
            if(!!target.value_end && filters.value_start !== ''){
                firstMonthDate = moment(target.value_end).startOf('month').format('YYYY-MM-DD');
            }
            
            filters.value_start = !!target.value_start ? target.value_start : firstMonthDate;
            filters.value_end = !!target.value_end ? target.value_end : now;
        }
        filters = clearFilters(target,filters);
        setFilterValues(filters)
        
        await updateSession();
    }

    const filters = useMemo(() => ({
        validateOnChange: true,
        showGoBackButton: false,
        showSubmitButton: false,
        update_key: `${filterValues.pdc_pconta_category_id}-${filterValues.pdc_project_id}-${filterValues.pdc_entity_id}-${filterValues.pdc_pconta_origin_resource_id}-${filterValues.pdc_pconta_sub_category_id}-${filterValues.value_start}-${filterValues.value_end}`,
        handleSubmit: loadData,
        fields: [
            {
                name: 'pdc_entity_id',
                type: 'select',
                label: 'Entidade',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllEntities,
                    label: 'corporate_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'pdc_project_id',
                type: 'select',
                label: 'Projeto',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllPdcProjects,
                    label: 'project_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter,
                            pdc_entity_id: !!filterValues.pdc_entity_id ? filterValues.pdc_entity_id : profileFilter.pdc_entity_id 
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'pdc_pconta_origin_resource_id',
                type: 'select',
                label: 'Origem dos Recursos',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllOriginResources,
                    label: 'description',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter,
                            ...filterValues
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'period',
                name_start: 'value_start',
                name_end: 'value_end',
                type: 'date_period',
                label: 'Periodo',
                handleChange,
                validations: {
                    required: false,
                },
                columnSize: 3,
            },
        ],
        values: {
            pdc_entity_id: filterValues.pdc_entity_id,
            pdc_project_id: filterValues.pdc_project_id,
            pdc_pconta_origin_resource_id: filterValues.pdc_pconta_origin_resource_id,
            pdc_pconta_category_id: filterValues.pdc_pconta_category_id,
            pdc_pconta_sub_category_id: filterValues.pdc_pconta_sub_category_id,
            value_start: filterValues.value_start,
            value_end: filterValues.value_end
        }
    }), [paths, filterValues]);

    const list = useMemo(() => {
        let list = {
            labelTitle: 'Lista de contas a receber PCONTAS',
            labelTitleButtonAdd: 'Incluir nova conta a pagar',
            fieldsShowOnDelete: ['entry_sequence'],
            formFilters: filters,
            showPrintButtons: true,
            canDelete: true,
            canEdit: false,
            filter: {
                ...profileFilter,
                pdc_entity_id: !!filterValues.pdc_entity_id ? filterValues.pdc_entity_id : profileFilter.pdc_entity_id,
                pdc_project_id: filterValues.pdc_project_id,
                pdc_pconta_origin_resource_id: filterValues.pdc_pconta_origin_resource_id,
                pdc_pconta_category_id: filterValues.pdc_pconta_category_id,
                pdc_pconta_sub_category_id: filterValues.pdc_pconta_sub_category_id,
                value_start: filterValues.value_start,
                value_end: filterValues.value_end
            },
            columns: [
                {
                    name: 'entry_sequence',
                    label: 'Sequencia',
                    width: '10%'
                },
                {
                    name: 'despro',
                    label: 'Projeto Contabil',
                    width: '10%'
                },
                {
                    name: 'payment_value',
                    label: 'Valor Conta',
                    width: '10%'
                },
                {
                    name: 'payment_interest',
                    label: 'Valor Juros',
                    width: '10%'
                },
                {
                    name: 'payment_discount',
                    label: 'Valor Desconto',
                    width: '10%'
                },
                {
                    name: 'due_date',
                    label: 'Data de Vencimento',
                    width: '10%'
                },
                {
                    name: 'competence_date',
                    label: 'Data Competência',
                    width: '10%'
                },
                {
                    name: 'issue_date',
                    label: 'Data de Emissão',
                    width: '10%'
                },
            ]
        }
    
        list = { ...list, ...paths };

        return list;

    },[paths, filters]);

    const content = useMemo(() => (
        <CustomList list={{ ...list, ...paths }} />
    ), [list, paths, filters]);

    const customListContainer = useMemo(() => (
        <CustomListContainer
            canAdd={false}
            pathAdd={paths.pathAdd}
            labelTitle={list.labelTitle}
            labelTitleButtonAdd={list.labelTitleButtonAdd}
            content={content}
        />
    ), [content, paths, filters, list]);

    return (
        <div>
            {customListContainer}
        </div>
    );
}
export default List;
